import React, { useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Home, ChevronRight, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '../../App';

/**
 * Smart Back Navigation Logic:
 * - If there's meaningful browser history, use navigate(-1) to go back
 * - If user landed directly on this page (no history), go to fallback URL
 * 
 * Detection method: 
 * - Track navigation within the app using sessionStorage
 * - This is more reliable than document.referrer for SPAs
 */

// Key for tracking internal navigation
const NAV_HISTORY_KEY = 'app_nav_history';

// Helper to track that user navigated within the app
const trackNavigation = () => {
  try {
    const count = parseInt(sessionStorage.getItem(NAV_HISTORY_KEY) || '0', 10);
    sessionStorage.setItem(NAV_HISTORY_KEY, String(count + 1));
  } catch (e) {
    // sessionStorage might be blocked
  }
};

// Helper to check if user has navigated within the app
const hasInternalHistory = () => {
  try {
    const count = parseInt(sessionStorage.getItem(NAV_HISTORY_KEY) || '0', 10);
    return count > 0;
  } catch (e) {
    return false;
  }
};

// Helper to decrement navigation count (when going back)
const decrementNavHistory = () => {
  try {
    const count = parseInt(sessionStorage.getItem(NAV_HISTORY_KEY) || '0', 10);
    if (count > 0) {
      sessionStorage.setItem(NAV_HISTORY_KEY, String(count - 1));
    }
  } catch (e) {
    // sessionStorage might be blocked
  }
};

const useSmartBack = (fallbackUrl = '/') => {
  const navigate = useNavigate();
  
  const handleBack = useCallback(() => {
    // Check if user has navigated within our app
    const hasHistory = hasInternalHistory();
    
    // Also check browser history length as backup
    const hasBrowserHistory = window.history.length > 1;
    
    if (hasHistory && hasBrowserHistory) {
      // Safe to go back - user has navigated within our app
      decrementNavHistory();
      navigate(-1);
    } else {
      // User likely landed directly on this page - go to fallback
      navigate(fallbackUrl);
    }
  }, [navigate, fallbackUrl]);
  
  return handleBack;
};

/**
 * Breadcrumbs - Reusable navigation breadcrumbs component with Back button
 * 
 * Features:
 * - Smart back button (internal nav → history, external → fallback)
 * - Pill-style items with hover effects
 * - Home icon for first item
 * - Last item rendered as span (not clickable)
 * - Responsive: Mobile shows only back button, Desktop shows back + breadcrumbs
 * 
 * @param {Array} items - Array of breadcrumb items: { label: string, href?: string }
 * @param {string} className - Additional classes for the container
 * @param {boolean} showHomeIcon - Whether to show the Home icon (default: true)
 * @param {boolean} showBackButton - Whether to show the back button (default: true)
 * @param {string} fallbackUrl - URL to navigate to when coming from external source (default: first breadcrumb href or '/')
 */
const Breadcrumbs = ({ 
  items = [], 
  className,
  showHomeIcon = true,
  showBackButton = true,
  fallbackUrl,
  'data-testid': dataTestId = 'breadcrumbs'
}) => {
  const { language } = useLanguage();
  
  // Determine fallback URL: use provided, or second-to-last breadcrumb href, or homepage
  const determinedFallback = fallbackUrl 
    || (items.length > 1 ? items[items.length - 2]?.href : null) 
    || '/';
  
  const handleBack = useSmartBack(determinedFallback);
  
  // Translations for back button
  const backLabel = language === 'nl' ? 'Terug' : language === 'es' ? 'Volver' : 'Back';
  
  if (!items || items.length === 0) {
    // If no breadcrumb items but showBackButton is true, still show back button
    if (showBackButton) {
      return (
        <nav 
          className={cn("flex items-center gap-1.5 text-sm py-1 -my-1", className)}
          data-testid={dataTestId}
          aria-label="Navigation"
        >
          <button
            onClick={handleBack}
            className="flex items-center gap-1.5 px-2 py-1.5 text-muted-foreground hover:text-primary hover:bg-secondary/50 rounded-lg transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            <span>{backLabel}</span>
          </button>
        </nav>
      );
    }
    return null;
  }

  return (
    <nav 
      className={cn(
        "flex items-center gap-1.5 text-sm py-1 -my-1",
        className
      )}
      data-testid={dataTestId}
      aria-label="Breadcrumb"
    >
      {/* Back Button - Always visible on mobile, optional on desktop */}
      {showBackButton && (
        <>
          {/* Mobile: Back button with text */}
          <button
            onClick={handleBack}
            className="md:hidden flex items-center gap-1.5 px-2 py-1.5 text-muted-foreground hover:text-primary hover:bg-secondary/50 rounded-lg transition-colors"
            data-testid="back-button-mobile"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            <span>{backLabel}</span>
          </button>
          
          {/* Desktop: Back arrow only (breadcrumbs provide context) */}
          <button
            onClick={handleBack}
            className="hidden md:flex items-center justify-center w-8 h-8 text-muted-foreground hover:text-primary hover:bg-secondary/50 rounded-lg transition-colors"
            data-testid="back-button-desktop"
            title={backLabel}
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
          </button>
          
          {/* Separator between back button and breadcrumbs (desktop only) */}
          <div className="hidden md:block w-px h-5 bg-border mx-1" aria-hidden="true" />
        </>
      )}
      
      {/* Breadcrumb items - Desktop only */}
      <div className="hidden md:flex items-center gap-1.5 overflow-x-auto min-w-0">
        {items.map((item, index) => {
          const isFirst = index === 0;
          const isLast = index === items.length - 1;
          const isHome = item.label === 'Home' || item.href === '/';
          
          return (
            <React.Fragment key={`${item.label}-${index}`}>
              {/* Separator (not before first item) */}
              {!isFirst && (
                <ChevronRight 
                  className="w-4 h-4 text-muted-foreground/50 shrink-0" 
                  aria-hidden="true"
                />
              )}
              
              {/* Breadcrumb Item */}
              {isLast || !item.href ? (
                // Last item or no href - render as span (not clickable)
                <span 
                  className={cn(
                    "px-2 py-1 shrink-0 capitalize",
                    isLast 
                      ? "font-medium text-foreground truncate max-w-[200px] sm:max-w-[300px]" 
                      : "text-muted-foreground"
                  )}
                  title={isLast ? item.label : undefined}
                >
                  {isHome && showHomeIcon ? (
                    <span className="flex items-center gap-1.5">
                      <Home className="w-4 h-4" aria-hidden="true" />
                      <span className="hidden sm:inline">{item.label}</span>
                    </span>
                  ) : (
                    item.label
                  )}
                </span>
              ) : (
                // Clickable link item
                <Link
                  to={item.href}
                  className={cn(
                    "px-2 py-1 text-muted-foreground hover:text-primary hover:bg-secondary/50 rounded-lg transition-colors shrink-0 capitalize",
                    "focus:outline-none focus:ring-2 focus:ring-primary/50 focus:ring-offset-1"
                  )}
                >
                  {isHome && showHomeIcon ? (
                    <span className="flex items-center gap-1.5">
                      <Home className="w-4 h-4" aria-hidden="true" />
                      <span className="hidden sm:inline">{item.label}</span>
                    </span>
                  ) : (
                    item.label
                  )}
                </Link>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </nav>
  );
};

// Export the hook and helper for use in other components
export { Breadcrumbs, useSmartBack, trackNavigation };
export default Breadcrumbs;
